/**
 * Validation rules shared by the browser forms and the server handlers.
 *
 * Deliberately dependency-free CommonJS. Webpack bundles this into the React
 * app and Node `require`s it from the API handlers; importing a library here
 * would drag in ESM/CJS interop differences between those two runtimes, so the
 * rules are written in plain JavaScript instead. One definition, both sides,
 * no build step.
 *
 * Each validator returns { data, errors }:
 *   data   - trimmed values, safe to use
 *   errors - { fieldName: 'message' }; empty object means valid
 */

// Bots fill in every field they find. A real person never sees this one.
const HONEYPOT_FIELD = 'company_website';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

const text = (value) => (typeof value === 'string' ? value.trim() : '');

function checkName(value) {
  if (!value) return 'Please enter your name.';
  if (value.length > 100) return 'Please keep your name under 100 characters.';
  return null;
}

function checkEmail(value) {
  if (!value) return 'Please enter your email.';
  if (value.length > 254) return 'Please keep your email under 254 characters.';
  if (!EMAIL_PATTERN.test(value)) return 'Please enter a valid email address.';
  return null;
}

function checkPhone(value) {
  if (!value) return 'Please enter your phone number.';
  const digits = value.replace(/\D/g, '');
  if (digits.length < 7 || digits.length > 15) return 'Please enter a valid phone number.';
  return null;
}

function checkMessage(value, { required }) {
  if (required && !value) return 'Please enter a message.';
  if (value.length > 5000) return 'Please keep your message under 5000 characters.';
  return null;
}

function collect(checks) {
  const errors = {};
  for (const [field, error] of Object.entries(checks)) {
    if (error) errors[field] = error;
  }
  return errors;
}

/** "Get in Touch" form */
function validateContact(values = {}) {
  const data = {
    name: text(values.name),
    email: text(values.email),
    message: text(values.message)
  };

  return {
    data,
    errors: collect({
      name: checkName(data.name),
      email: checkEmail(data.email),
      message: checkMessage(data.message, { required: true })
    })
  };
}

/** "Request for Demo" dialog — the fields a visitor types */
function validateDemoForm(values = {}) {
  const data = {
    name: text(values.name),
    email: text(values.email),
    phone: text(values.phone),
    message: text(values.message)
  };

  return {
    data,
    errors: collect({
      name: checkName(data.name),
      email: checkEmail(data.email),
      phone: checkPhone(data.phone),
      message: checkMessage(data.message, { required: false })
    })
  };
}

/** What the server receives: the form fields plus the product that was clicked */
function validateDemoRequest(values = {}) {
  const { data, errors } = validateDemoForm(values);
  const productName = text(values.productName);

  if (!productName) errors.productName = 'Product is required.';
  else if (productName.length > 120) errors.productName = 'Product name is too long.';

  return { data: { ...data, productName }, errors };
}

/**
 * Adapter so react-hook-form can use these validators directly. RHF accepts any
 * function of this shape, so no resolver library is involved.
 */
function createResolver(validate) {
  return async (values) => {
    const { data, errors } = validate(values);
    const fieldErrors = {};

    for (const [field, message] of Object.entries(errors)) {
      fieldErrors[field] = { type: 'validation', message };
    }

    return Object.keys(fieldErrors).length > 0
      ? { values: {}, errors: fieldErrors }
      : { values: data, errors: {} };
  };
}

module.exports = {
  HONEYPOT_FIELD,
  validateContact,
  validateDemoForm,
  validateDemoRequest,
  createResolver
};
